import type {
  AutomationAccountOverride,
  AutomationDProp,
  AutomationEnvironment,
  AutomationFreshSource,
  AutomationRunConfig,
  AutomationSegment,
  AutomationTestType,
} from '@tms/shared';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Info,
  MessageSquarePlus,
  Play,
  RotateCcw,
  Terminal,
  User,
  UserCheck,
  UserPlus,
  X,
} from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { Badge, Button, Dialog, Input, SkeletonRows, Textarea } from '../../../components/ui';
import { useFormat, useT } from '../../../i18n';
import { cn } from '../../../lib/cn';
import { queryKeys } from '../../../lib/query-keys';
import {
  useClassNotes,
  usePoolAccounts,
  useReadiness,
  useRegistrationAccounts,
  useSetClassNote,
  useTestPlan,
} from '../hooks';
import type { PoolAccountSummary, PoolList, ReadinessMatrix } from '../testrun-types';
import { useRunsErrorText, useVerdictText } from './format';
import {
  accountOverrideProps,
  buildGradleCommand,
  PREPROD_SNIPPET,
  prettyTag,
  segmentOfClass,
} from './gradle';
import { recheckInTurn } from './hooks';
import { FitBadge, PoolPicker, SmallLabel } from './PoolPicker';
import {
  applyAccount,
  autoPick,
  findInPool,
  findReg,
  FRESH_REASON_PREFIX_RE,
  freshOf,
  pickFresh,
  pickRegistration,
  pickRotate,
  pickSingle,
  setField,
  unfit,
  verdictFor,
  type AccountLike,
  type AutoPickState,
  type ClassInfo,
  type Overrides,
} from './preflight';

/**
 * The pre-flight review of a run (TestRun src/components/RunPreflight.tsx):
 * the classes that will run, grouped by the account they go under; an account
 * per group or class from the pool, with readiness hints; automatic picks for
 * classes the given account does not fit, for one-off, rotating and
 * registration classes; class notes; classes removed from this run; the
 * command; and a re-check of the pool accounts before the start.
 */

/** What the run page chose — the dialog must honour all of it. */
export interface PreflightBase {
  gradleTask: string;
  type: NonNullable<AutomationRunConfig['type']>;
  suite?: string;
  classes?: string[];
  /** the raw @Tag values (not the prettyTag names) */
  domainTags: string[];
  /** IE only or LLP only (@Tag IP / TOO) */
  segment?: AutomationSegment;
  testType?: AutomationTestType;
  environment: AutomationEnvironment;
  browser?: string;
  device?: string;
  globalProps: AutomationDProp[];
}

interface RecheckProblem {
  account: string;
  text: string;
  classes: string[];
}

interface RecheckState {
  running: boolean;
  done: number;
  total: number;
  problems: RecheckProblem[];
  unknown: number;
}

const FRESH_SOURCES: ReadonlySet<string> = new Set<AutomationFreshSource>([
  'testdata',
  'guard',
  'tag',
  'displayName',
  'comment',
  'note',
]);

const EMPTY_AUTO: AutoPickState = {
  picked: {},
  kept: [],
  fresh: [],
  freshKept: [],
  rotate: [],
  reg: [],
  regKept: [],
};

const Notice = ({
  tone,
  icon,
  children,
}: {
  tone: 'fail' | 'block' | 'pass' | 'neutral';
  icon: ReactNode;
  children: ReactNode;
}) => (
  <div
    className={cn(
      'flex items-start gap-2 rounded-lg px-3 py-2.5 text-[12.5px] text-ink',
      tone === 'fail' && 'bg-fail-soft',
      tone === 'block' && 'bg-block-soft',
      tone === 'pass' && 'bg-pass-soft',
      tone === 'neutral' && 'border border-border bg-canvas',
    )}
  >
    <span
      className={cn(
        'mt-0.5 shrink-0',
        tone === 'fail' && 'text-fail',
        tone === 'block' && 'text-block',
        tone === 'pass' && 'text-pass',
        tone === 'neutral' && 'text-ink-subtle',
      )}
      aria-hidden="true"
    >
      {icon}
    </span>
    <div className="min-w-0 space-y-1">{children}</div>
  </div>
);

const Code = ({ children }: { children: ReactNode }) => (
  <code className="font-mono text-[11.5px]">{children}</code>
);

/** A link-looking button that opens a class row. */
const ClassLink = ({ name, onClick }: { name: string; onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className="mr-2 font-mono text-[12px] text-accent-fg underline hover:no-underline"
  >
    {name}
  </button>
);

/** An account field: phone, IIN or BIN, for a group or one class. */
const AccountField = ({
  label,
  value,
  placeholder,
  onChange,
}: {
  label: ReactNode;
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
}) => {
  const id = useId();
  return (
    <div className="flex min-w-[180px] flex-1 flex-col gap-1">
      <SmallLabel htmlFor={id}>{label}</SmallLabel>
      <Input
        id={id}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        className="h-8 font-mono text-[12.5px]"
      />
    </div>
  );
};

export function RunPreflight({
  open,
  onClose,
  projectId,
  base,
  onLaunch,
  pending = false,
}: {
  open: boolean;
  onClose: () => void;
  projectId: string;
  base: PreflightBase;
  onLaunch: (config: AutomationRunConfig) => void;
  pending?: boolean;
}) {
  const { t } = useT();
  const { date } = useFormat();
  const queryClient = useQueryClient();
  const errorText = useRunsErrorText();
  const { text: verdictText } = useVerdictText();
  const noteId = useId();

  const planQuery = useTestPlan(projectId);
  const plan = planQuery.data ?? null;
  const notesQuery = useClassNotes(projectId);
  const notes = notesQuery.data;
  const notesReady = notesQuery.isFetched;
  const saveNote = useSetClassNote(projectId);
  /* The pool and «ready for tests»: a group's account is picked from the pool, the
   * ones that fit its classes on top. No plan or no pool — the choice goes
   * without hints, and typing an account by hand stays. */
  const poolQuery = usePoolAccounts(open);
  const pool = poolQuery.data;
  const readinessQuery = useReadiness(projectId, open);
  const readiness: ReadinessMatrix | undefined = readinessQuery.data ?? undefined;
  /* Registration classes (testdata registration) get an account from «For
   * registration»: the pool holds registered clients only. Asked only when the
   * plan has such classes. */
  const needsRegistration = (plan?.classes ?? []).some((c) => c.registration);
  const regsQuery = useRegistrationAccounts(open && needsRegistration);
  const regs = regsQuery.data;

  /* Before the run — a re-check of the pool accounts the classes go under: a card
   * may have been blocked, a deposit closed. The check uses the token TestRun keeps,
   * without a new OTP. */
  const [recheckFirst, setRecheckFirst] = useState(true);
  const [rc, setRc] = useState<RecheckState | null>(null);
  // note drafts: until saved, edits live here
  const [noteDraft, setNoteDraft] = useState<Record<string, string>>({});
  const [ov, setOv] = useState<Overrides>({});
  const [auto, setAuto] = useState<AutoPickState | null>(null);
  const autoKey = useRef('');
  const [openClass, setOpenClass] = useState<string | null>(null);
  const [showSnippet, setShowSnippet] = useState(false);
  // classes removed from this run with the cross
  const [excluded, setExcluded] = useState<string[]>([]);

  // The classes chosen on the run page: the classes or suite first, then the
  // domain tag and segment filters. The run gets them minus the removed ones.
  const selectedClasses = useMemo(() => {
    let list = plan?.classes ?? [];
    if (base.type === 'classes') {
      const picked = new Set(base.classes ?? []);
      list = list.filter((c) => picked.has(c.class));
    } else if (base.type === 'suite' && base.suite) {
      const suite = base.suite;
      list = list.filter(
        (c) => c.tags.some((tag) => prettyTag(tag) === suite) || c.tags.includes(suite),
      );
    }
    if (base.domainTags.length)
      list = list.filter((c) => c.tags.some((tag) => base.domainTags.includes(tag)));
    if (base.segment) list = list.filter((c) => segmentOfClass(c) === base.segment);
    return list;
  }, [plan, base.type, base.suite, base.classes, base.domainTags, base.segment]);
  const runClasses = useMemo(
    () => selectedClasses.filter((c) => !excluded.includes(c.class)),
    [selectedClasses, excluded],
  );
  const excludedClasses = selectedClasses.filter((c) => excluded.includes(c.class));

  const effPhone = (c: ClassInfo) => ov[c.class]?.phone ?? c.account.phone ?? '';
  const effIin = (c: ClassInfo) => ov[c.class]?.iin ?? c.account.iin ?? '';
  // only LLP cases have a BIN: an IE's field is empty and not worth showing
  const effBin = (c: ClassInfo) => ov[c.class]?.bin ?? c.account.bin ?? '';
  // the pool account a class goes under now (by phone)
  const poolOf = (c: ClassInfo) => findInPool(pool?.accounts, effPhone(c));
  // the «for registration» account a class goes under now (by phone and IIN)
  const regOf = (c: ClassInfo) =>
    c.registration ? findReg(regs?.accounts, effPhone(c), effIin(c)) : undefined;
  /* A group's value: common to all its classes, otherwise empty — after a pick
   * or a per-class edit they differ, and showing the first class's would be untrue. */
  const groupValue = (classes: ClassInfo[], get: (c: ClassInfo) => string) => {
    const first = classes[0];
    return first && classes.every((c) => get(c) === get(first)) ? get(first) : '';
  };

  const fresh = (c: ClassInfo) => freshOf(c, notes);
  // an account set in this run — for the class or its group
  const hasOwnAccount = (c: ClassInfo) =>
    Boolean(ov[c.class]?.phone || ov[c.class]?.iin || ov[c.class]?.bin);
  /* Classes that keep «Run» disabled: they need a new account and go under the
   * scanned one. The way out is to set an account or remove the class. */
  const blocked = runClasses.filter((c) => fresh(c) && !hasOwnAccount(c));
  const exclude = (name: string) => {
    setExcluded((xs) => (xs.includes(name) ? xs : [...xs, name]));
    if (openClass === name) setOpenClass(null);
  };

  // IE first, then LLP, inside by the SCANNED account (stable, so an edit does not
  // reshuffle rows). One account in IE and LLP is two groups: an IE override must
  // not silently move to LLP.
  const groups = useMemo(() => {
    const order = { IP: 0, TOO: 1, none: 2 } as const;
    const map = new Map<string, { segment: AutomationSegment | null; classes: ClassInfo[] }>();
    for (const c of runClasses) {
      const segment = segmentOfClass(c);
      const key = `${segment ?? 'none'}|${c.account.phone ?? '—'}|${c.account.iin ?? '—'}|${c.account.bin ?? '—'}`;
      const entry = map.get(key) ?? { segment, classes: [] };
      entry.classes.push(c);
      map.set(key, entry);
    }
    return [...map.entries()]
      .map(([key, g]) => ({ key, ...g }))
      .sort((a, b) => order[a.segment ?? 'none'] - order[b.segment ?? 'none']);
  }, [runClasses]);
  const hasSegments = groups.some((g) => g.segment);

  /* Classes chosen on the form but missing from the scanned plan: a stale catalogue —
   * the class was renamed in the repository and stayed in the list. Silently
   * showing «nothing matched» would not say what to fix. */
  const missingClasses =
    base.type === 'classes'
      ? (base.classes ?? []).filter((n) => !(plan?.classes ?? []).some((c) => c.class === n))
      : [];

  const caseCount = runClasses.reduce((sum, c) => sum + c.cases.length, 0);
  const overridesArr: AutomationAccountOverride[] = Object.entries(ov)
    .filter(([, v]) => v.phone || v.iin || v.bin)
    .map(([className, v]) => ({ className, ...v }));
  const distinctAccounts = new Set(
    runClasses.map((c) => `${effPhone(c)}|${effIin(c)}|${effBin(c)}`),
  ).size;

  /* A project with a CaseData file keeps case data in it, not in -D. The file is
   * testdata.json for some and testdata.yaml for others — compared by prefix. */
  const isTestdata = Boolean(plan?.source?.startsWith('testdata'));

  /* The automatic pick when the dialog opens: a class with requires that the given
   * account surely does not fit (it is in the pool and the check says «no: …») gets
   * a fitting pool account; none fits, or the given one is not from the pool — it
   * stays. Only where CaseData reads per-class overrides: otherwise the pick would
   * not reach the tests. Once per set of classes — «Restore the given ones» is not
   * overridden by another pick. */
  const autoAllowed = isTestdata && Boolean(plan?.perClassOverrides);
  // one account for the whole run (testrun.singleAccount): the choice and the pick go to all classes
  const single = Boolean(plan?.singleAccount);
  useEffect(() => {
    if (!open || !autoAllowed || !pool || !readiness || !notesReady) return;
    if (selectedClasses.some((c) => c.registration) && !regs) return;
    const key = `${projectId}|${selectedClasses.map((c) => c.class).join(',')}`;
    if (autoKey.current === key) return;
    autoKey.current = key;
    if (single) {
      const result = pickSingle(
        runClasses.filter((c) => !hasOwnAccount(c)),
        pool.accounts,
        readiness,
        effPhone,
      );
      if (!result) {
        setAuto(null);
        return;
      }
      const pick = result.pick;
      if (!pick) {
        setAuto({ ...EMPTY_AUTO, kept: runClasses.map((c) => c.class) });
        return;
      }
      setOv((prev) => runClasses.reduce((next, c) => applyAccount(next, c, pick), prev));
      setAuto({
        ...EMPTY_AUTO,
        picked: Object.fromEntries(runClasses.map((c) => [c.class, pick.id])),
      });
      return;
    }
    const general = autoPick(
      runClasses.filter((c) => !fresh(c) && !hasOwnAccount(c)),
      pool.accounts,
      readiness,
      effPhone,
    );
    // accounts the other classes of the run go under are not given to one-off classes:
    // a destructive case would spoil them
    const taken = new Set(
      runClasses
        .filter((c) => !fresh(c))
        .map((c) => (general.picks[c.class] ?? findInPool(pool.accounts, effPhone(c)))?.id)
        .filter((id): id is string => Boolean(id)),
    );
    const freshPicks = pickFresh(
      runClasses.filter((c) => fresh(c) && !c.registration && !hasOwnAccount(c)),
      pool.accounts,
      readiness,
      taken,
    );
    for (const a of Object.values(freshPicks.picks)) taken.add(a.id);
    const rotate = pickRotate(
      runClasses.filter((c) => c.rotateAccount && !fresh(c) && !hasOwnAccount(c)),
      pool.accounts,
      readiness,
      taken,
    );
    const reg = pickRegistration(
      runClasses.filter((c) => c.registration && !hasOwnAccount(c)),
      regs?.accounts ?? [],
    );
    const picks: Record<string, AccountLike> = {
      ...general.picks,
      ...freshPicks.picks,
      ...rotate.picks,
      ...reg.picks,
    };
    if (
      !Object.keys(picks).length &&
      !general.kept.length &&
      !freshPicks.kept.length &&
      !reg.kept.length
    ) {
      setAuto(null);
      return;
    }
    setOv((prev) =>
      runClasses.reduce((next, c) => {
        const pick = picks[c.class];
        return pick ? applyAccount(next, c, pick) : next;
      }, prev),
    );
    setAuto({
      picked: Object.fromEntries(Object.entries(picks).map(([k, a]) => [k, a.id])),
      kept: [...general.kept, ...rotate.kept],
      fresh: Object.keys(freshPicks.picks),
      freshKept: freshPicks.kept,
      rotate: Object.keys(rotate.picks),
      reg: Object.keys(reg.picks),
      regKept: reg.kept,
    });
    // once per set of classes (autoKey): the overrides and notes it reads are this render's
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, autoAllowed, pool, readiness, notesReady, projectId, selectedClasses, regs]);

  const autoPickedNow = (c: ClassInfo) =>
    Boolean(auto?.picked[c.class]) && (poolOf(c) ?? regOf(c))?.id === auto?.picked[c.class];
  const revertAuto = () => {
    if (!auto) return;
    setOv((prev) => {
      const next = { ...prev };
      for (const c of runClasses) if (autoPickedNow(c)) delete next[c.class];
      return next;
    });
    setAuto(null);
  };

  const accountProps = isTestdata ? [] : accountOverrideProps(overridesArr);
  const allProps = [...base.globalProps, ...accountProps];
  // the command takes exactly the classes shown above, or the preview promises one thing
  // and the runner does another
  const runClassNames = runClasses.map((c) => c.class);
  /* Gradle's --tests only includes. To remove a class from a suite or tag run, the
   * run becomes an explicit list of the remaining classes; tags and segment stay
   * filters on top. */
  const explicitClasses = base.type === 'classes' || excluded.length > 0;
  const command = buildGradleCommand({
    task: base.gradleTask,
    domainTags: base.domainTags,
    segment: base.segment ?? null,
    classes: explicitClasses ? runClassNames : null,
    props: allProps,
  });

  const setGroup = (classes: ClassInfo[], field: 'phone' | 'iin' | 'bin', value: string) =>
    setOv((prev) => setField(prev, classes, field, value));
  const setOne = (c: ClassInfo, field: 'phone' | 'iin' | 'bin', value: string) =>
    setOv((prev) => setField(prev, [c], field, value));
  const pickFor = (classes: ClassInfo[], a: PoolAccountSummary) =>
    setOv((prev) => {
      let next = setField(prev, classes, 'phone', a.phone);
      next = setField(next, classes, 'iin', a.iin);
      return setField(next, classes, 'bin', a.bin ?? '');
    });

  // edited accounts or classes — the last re-check is no longer about this run
  useEffect(() => setRc(null), [ov, excluded]);

  const runPoolAccounts = [
    ...new Map(
      runClasses.flatMap((c) => {
        const a = poolOf(c);
        return a ? [[a.id, a] as const] : [];
      }),
    ).values(),
  ];

  // one-off classes under a pool account: TestRun marks the account spent for the class
  const spendAccounts = runClasses.flatMap((c) => {
    const a = fresh(c) ? poolOf(c) : undefined;
    return a ? [{ className: c.class, accountId: a.id }] : [];
  });
  // registration classes under a free «for registration» account: TestRun marks it issued to the run
  const issueRegistration = runClasses.flatMap((c) => {
    const r = regOf(c);
    return r && r.status === 'free' ? [{ className: c.class, accountId: r.id }] : [];
  });
  // every class under a pool account: TestRun remembers lastUsed — rotation takes the next by it
  const usedAccounts = runClasses.flatMap((c) => {
    const a = poolOf(c);
    return a ? [{ className: c.class, accountId: a.id }] : [];
  });

  const launch = () => {
    const type = explicitClasses ? 'classes' : base.type;
    onLaunch({
      type,
      ...(type === 'suite' && base.suite ? { suite: base.suite } : {}),
      ...(explicitClasses ? { classes: runClassNames } : {}),
      ...(base.testType ? { testType: base.testType } : {}),
      environment: base.environment,
      ...(base.browser ? { browser: base.browser } : {}),
      ...(base.device ? { device: base.device } : {}),
      gradleTask: base.gradleTask,
      ...(base.domainTags.length ? { domainTags: base.domainTags } : {}),
      ...(base.segment ? { segment: base.segment } : {}),
      accountOverrides: overridesArr,
      ...(spendAccounts.length ? { spendAccounts } : {}),
      ...(usedAccounts.length ? { usedAccounts } : {}),
      ...(issueRegistration.length ? { issueRegistration } : {}),
    });
  };

  const recheckAndLaunch = async () => {
    if (!recheckFirst || !runPoolAccounts.length) {
      launch();
      return;
    }
    setRc({ running: true, done: 0, total: runPoolAccounts.length, problems: [], unknown: 0 });
    // one by one, as «check all»: the stand limits logins
    const failed = await recheckInTurn(queryClient, runPoolAccounts, (done) =>
      setRc((r) => r && { ...r, done }),
    );
    // the pool and the matrix read again, once
    await queryClient.invalidateQueries({ queryKey: queryKeys.automation.testRun() });
    const freshPool =
      queryClient.getQueryData<PoolList>(queryKeys.automation.testRun('pool')) ?? pool;
    const freshMatrix =
      queryClient.getQueryData<ReadinessMatrix | null>(
        queryKeys.automation.testRun('readiness', projectId),
      ) ?? undefined;
    // one line per «account + reason»: a login failure is all of the account's classes at once
    const byReason = new Map<string, RecheckProblem>();
    const add = (account: string, text: string, cls: string) => {
      const key = `${account}|${text}`;
      const problem = byReason.get(key) ?? { account, text, classes: [] };
      problem.classes.push(cls);
      byReason.set(key, problem);
    };
    let unknown = 0;
    for (const c of runClasses) {
      const a = findInPool(freshPool?.accounts, effPhone(c));
      if (!a) continue;
      const account = a.companyName ?? a.label;
      if (failed.has(a.id)) {
        add(
          account,
          t('automation.runs.preflight.recheckFailed', { error: errorText(failed.get(a.id)) }),
          c.class,
        );
        continue;
      }
      const v = verdictFor(freshMatrix, c.class, a);
      if (v && unfit(v)) add(account, verdictText(v), c.class);
      else if (v?.kind === 'unknown') unknown++;
    }
    const problems = [...byReason.values()];
    if (!problems.length) {
      setRc(null);
      launch();
      return;
    }
    setRc({
      running: false,
      done: runPoolAccounts.length,
      total: runPoolAccounts.length,
      problems,
      unknown,
    });
  };

  const saveClassNote = (className: string) =>
    saveNote.mutate(
      { className, note: noteDraft[className] ?? '' },
      {
        onSuccess: () =>
          setNoteDraft((drafts) => {
            const next = { ...drafts };
            delete next[className];
            return next;
          }),
        onError: (error) => toast.error(errorText(error)),
      },
    );

  const generalPicked = auto
    ? Object.keys(auto.picked).length - auto.fresh.length - auto.rotate.length - auto.reg.length
    : 0;
  const showAuto =
    auto !== null &&
    (Object.keys(auto.picked).length > 0 ||
      auto.kept.length > 0 ||
      auto.freshKept.length > 0 ||
      auto.regKept.length > 0);
  const problemCount = rc?.problems.reduce((n, p) => n + p.classes.length, 0) ?? 0;

  const footer = plan ? (
    <>
      <label
        className="mr-auto flex items-center gap-2 text-[12px] text-ink-muted"
        title={t('automation.runs.preflight.recheckTitle')}
      >
        <input
          type="checkbox"
          checked={recheckFirst}
          onChange={(event) => setRecheckFirst(event.target.checked)}
          disabled={!runPoolAccounts.length}
          className="accent-accent"
        />
        {runPoolAccounts.length
          ? t('automation.runs.preflight.recheck', { count: runPoolAccounts.length })
          : t('automation.runs.preflight.recheckNothing')}
      </label>
      <Button onClick={onClose}>{t('common.cancel')}</Button>
      <Button
        variant="primary"
        disabled={pending || Boolean(rc?.running) || runClasses.length === 0 || blocked.length > 0}
        onClick={rc?.problems.length ? launch : () => void recheckAndLaunch()}
        title={blocked.length > 0 ? t('automation.runs.preflight.blockedLaunch') : undefined}
      >
        {pending ? (
          t('automation.runs.preflight.creating')
        ) : rc?.running ? (
          t('automation.runs.preflight.rechecking', { done: rc.done, total: rc.total })
        ) : (
          <>
            <Play className="size-4" aria-hidden="true" />
            {rc?.problems.length
              ? t('automation.runs.preflight.launchAnyway')
              : t('automation.runs.preflight.launch', { count: caseCount })}
          </>
        )}
      </Button>
    </>
  ) : undefined;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t('automation.runs.preflight.title')}
      size="lg"
      footer={footer}
    >
      {planQuery.isPending ? (
        <SkeletonRows rows={4} className="p-0" />
      ) : !plan ? (
        <p className="text-[13px] text-ink-muted">{t('automation.runs.preflight.noPlan')}</p>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] text-ink">
            <span>{t('automation.runs.preflight.classCount', { count: runClasses.length })}</span>
            <span>{t('automation.runs.preflight.caseCount', { count: caseCount })}</span>
            <span>{t('automation.runs.preflight.accountCount', { count: distinctAccounts })}</span>
            {excluded.length > 0 ? (
              <span className="text-ink-muted">
                {t('automation.runs.preflight.excludedCount', { count: excluded.length })}
              </span>
            ) : null}
            {base.type === 'classes' ? (
              <Badge>{t('automation.runs.preflight.chosenClasses')}</Badge>
            ) : null}
            {base.type === 'suite' && base.suite ? <Badge>{base.suite}</Badge> : null}
            {base.domainTags.map((tag) => (
              <Badge key={tag} className="font-mono">
                {tag}
              </Badge>
            ))}
            {base.segment ? <Badge>{t(`automation.runs.segment.${base.segment}`)}</Badge> : null}
          </div>

          <p className="text-[13px] text-ink-muted">{t('automation.runs.preflight.intro')}</p>

          {poolQuery.error ? (
            <Notice tone="block" icon={<AlertTriangle className="size-4" />}>
              <p>
                {t('automation.runs.preflight.poolUnavailable', {
                  error: errorText(poolQuery.error),
                })}
              </p>
            </Notice>
          ) : null}

          {showAuto && auto ? (
            <div className="flex flex-wrap items-start justify-between gap-2 rounded-lg border border-border bg-canvas px-3 py-2.5 text-[12.5px]">
              <div className="flex min-w-0 flex-1 items-start gap-2">
                <UserCheck className="mt-0.5 size-4 shrink-0 text-pass" aria-hidden="true" />
                <div className="min-w-0 space-y-1">
                  {single && Object.keys(auto.picked).length > 0 ? (
                    <p>{t('automation.runs.preflight.auto.single')}</p>
                  ) : null}
                  {auto.rotate.length > 0 ? (
                    <p>
                      {t('automation.runs.preflight.auto.rotate', { count: auto.rotate.length })}
                    </p>
                  ) : null}
                  {auto.reg.length > 0 ? (
                    <p>{t('automation.runs.preflight.auto.reg', { count: auto.reg.length })}</p>
                  ) : null}
                  {auto.regKept.length > 0 ? (
                    <p className="text-fail">
                      {t('automation.runs.preflight.auto.regKept', {
                        list: auto.regKept.join(', '),
                      })}
                    </p>
                  ) : null}
                  {!single && generalPicked > 0 ? (
                    <p>{t('automation.runs.preflight.auto.picked', { count: generalPicked })}</p>
                  ) : null}
                  {auto.fresh.length > 0 ? (
                    <p>{t('automation.runs.preflight.auto.fresh', { count: auto.fresh.length })}</p>
                  ) : null}
                  {auto.freshKept.length > 0 ? (
                    <p className="text-fail">
                      {t('automation.runs.preflight.auto.freshKept', {
                        list: auto.freshKept.join(', '),
                      })}
                    </p>
                  ) : null}
                  {auto.kept.length > 0 ? (
                    <p className="text-ink-muted">
                      {single
                        ? t('automation.runs.preflight.auto.keptSingle')
                        : t('automation.runs.preflight.auto.kept', {
                            count: auto.kept.length,
                            list: auto.kept.join(', '),
                          })}
                    </p>
                  ) : null}
                </div>
              </div>
              {Object.keys(auto.picked).length > 0 ? (
                <Button
                  size="sm"
                  onClick={revertAuto}
                  title={t('automation.runs.preflight.auto.revertHint')}
                >
                  <RotateCcw className="size-3.5" aria-hidden="true" />
                  {t('automation.runs.preflight.auto.revert')}
                </Button>
              ) : null}
            </div>
          ) : null}

          {missingClasses.length > 0 ? (
            <Notice tone="fail" icon={<AlertTriangle className="size-4" />}>
              <p>
                {t('automation.runs.preflight.missingClasses')}{' '}
                {missingClasses.map((n) => (
                  <Code key={n}>{n} </Code>
                ))}
                {t('automation.runs.preflight.missingClassesHint')}
              </p>
            </Notice>
          ) : null}

          {runClasses.length === 0 && missingClasses.length === 0 ? (
            <Notice tone="block" icon={<AlertTriangle className="size-4" />}>
              <p>
                {excluded.length > 0
                  ? t('automation.runs.preflight.allExcluded')
                  : t('automation.runs.preflight.nothingMatched')}
              </p>
            </Notice>
          ) : null}

          {overridesArr.length > 0 && isTestdata && plan.perClassOverrides ? (
            <Notice tone="pass" icon={<Info className="size-4" />}>
              <p>
                {t('automation.runs.preflight.overridesApply', { count: overridesArr.length })}{' '}
                <Code>-Dauth.&lt;field&gt;.&lt;Class&gt;</Code>
                {' — '}
                {t('automation.runs.preflight.overridesApplyHint')}
              </p>
            </Notice>
          ) : null}

          {/* warned only when the repository does not read per-class keys: elsewhere the
              override reaches the tests (see perClassOverrides in the scanner) */}
          {overridesArr.length > 0 && isTestdata && !plan.perClassOverrides ? (
            <Notice tone="block" icon={<AlertTriangle className="size-4" />}>
              <p>
                {t('automation.runs.preflight.overridesApply', { count: overridesArr.length })}{' '}
                <Code>-Dauth.&lt;field&gt;.&lt;Class&gt;</Code>
                {' — '}
                {t('automation.runs.preflight.overridesIgnored', {
                  file: plan.source ?? 'testdata',
                })}
              </p>
            </Notice>
          ) : null}
          {overridesArr.length > 0 && !isTestdata ? (
            <Notice tone="block" icon={<AlertTriangle className="size-4" />}>
              <p>
                {t('automation.runs.preflight.overridesSnippet', { count: overridesArr.length })}{' '}
                <Code>auth.phone.&lt;Class&gt;</Code> —{' '}
                <button
                  type="button"
                  aria-expanded={showSnippet}
                  onClick={() => setShowSnippet((s) => !s)}
                  className="text-accent-fg underline hover:no-underline"
                >
                  {t('automation.runs.preflight.showPatch')}
                </button>
              </p>
            </Notice>
          ) : null}
          {showSnippet && !isTestdata ? (
            <pre className="overflow-x-auto rounded-lg bg-sunken p-3 font-mono text-[12px] text-ink">
              {PREPROD_SNIPPET}
            </pre>
          ) : null}

          {single && pool && pool.accounts.length > 0 ? (
            <div className="space-y-2 rounded-lg border border-border px-3 py-2.5 text-[12.5px]">
              <p>{t('automation.runs.preflight.single')}</p>
              <PoolPicker
                label={t('automation.runs.preflight.pickerRun')}
                classes={runClasses}
                segment={null}
                pool={pool.accounts}
                readiness={readiness}
                current={(() => {
                  const first = runClasses[0];
                  const a = first ? poolOf(first) : undefined;
                  return runClasses.every((c) => poolOf(c)?.id === a?.id) ? a : undefined;
                })()}
                mixed={(() => {
                  const first = runClasses[0];
                  return first ? !runClasses.every((c) => effPhone(c) === effPhone(first)) : false;
                })()}
                onPick={(a) =>
                  setOv((prev) => runClasses.reduce((next, c) => applyAccount(next, c, a), prev))
                }
              />
            </div>
          ) : null}

          {/* the account groups */}
          <div className="space-y-3">
            {groups.map(({ key, segment, classes }, gi) => {
              const first = classes[0];
              if (!first) return null;
              const sectionStart = hasSegments && (gi === 0 || groups[gi - 1]?.segment !== segment);
              const inSection = groups.filter((g) => g.segment === segment);
              const groupCurrent = classes.every((c) => poolOf(c)?.id === poolOf(first)?.id)
                ? poolOf(first)
                : undefined;
              return (
                <div key={key} className="space-y-2">
                  {sectionStart ? (
                    <h3
                      className={cn(
                        'text-[12px] font-semibold uppercase tracking-wide text-ink-muted',
                        gi > 0 && 'pt-2',
                      )}
                    >
                      {segment
                        ? t(`automation.runs.segment.${segment}`)
                        : t('automation.runs.preflight.noSegment')}
                      <span className="font-normal normal-case tracking-normal text-ink-subtle">
                        {' · '}
                        {t('automation.runs.preflight.classesCases', {
                          classes: inSection.reduce((s, g) => s + g.classes.length, 0),
                          cases: inSection.reduce(
                            (s, g) => s + g.classes.reduce((n, c) => n + (c.cases.length || 1), 0),
                            0,
                          ),
                        })}
                      </span>
                    </h3>
                  ) : null}
                  <div className="overflow-hidden rounded-xl border border-border">
                    <div className="space-y-2 bg-canvas px-3.5 py-3">
                      <p className="flex flex-wrap items-center gap-2 text-[13px] font-semibold text-ink">
                        <User className="size-4 text-accent-fg" aria-hidden="true" />
                        <span className="font-mono">{first.account.phone ?? '—'}</span>
                        <span className="font-normal text-ink-subtle">
                          ·{' '}
                          {t('automation.runs.preflight.classesCases', {
                            classes: classes.length,
                            cases: classes.reduce((s, c) => s + (c.cases.length || 1), 0),
                          })}
                        </span>
                        {first.account.origin === 'override' ? (
                          <Badge>{t('automation.runs.preflight.override')}</Badge>
                        ) : null}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <AccountField
                          label={t('automation.runs.preflight.groupField', { field: 'auth.phone' })}
                          value={groupValue(classes, effPhone)}
                          placeholder={t('automation.runs.preflight.mixed')}
                          onChange={(value) => setGroup(classes, 'phone', value)}
                        />
                        <AccountField
                          label={t('automation.runs.preflight.groupField', { field: 'auth.iin' })}
                          value={groupValue(classes, effIin)}
                          placeholder={t('automation.runs.preflight.mixed')}
                          onChange={(value) => setGroup(classes, 'iin', value)}
                        />
                        {effBin(first) ? (
                          <AccountField
                            label={t('automation.runs.preflight.groupField', { field: 'auth.bin' })}
                            value={groupValue(classes, effBin)}
                            placeholder={t('automation.runs.preflight.mixed')}
                            onChange={(value) => setGroup(classes, 'bin', value)}
                          />
                        ) : null}
                      </div>
                      {!single && pool && pool.accounts.length > 0 ? (
                        <PoolPicker
                          label={t('automation.runs.preflight.pickerGroup')}
                          classes={classes}
                          segment={segment}
                          pool={pool.accounts}
                          readiness={readiness}
                          current={groupCurrent}
                          mixed={!classes.every((c) => effPhone(c) === effPhone(first))}
                          onPick={(a) => pickFor(classes, a)}
                        />
                      ) : null}
                    </div>

                    <ul>
                      {classes.map((c) => {
                        const isOpen = openClass === c.class;
                        const changed = Boolean(ov[c.class]);
                        const freshInfo = fresh(c);
                        const note = notes?.[c.class];
                        const draft = noteDraft[c.class] ?? note?.text ?? '';
                        return (
                          <li key={c.class} className="border-t border-border">
                            <div className="flex items-stretch">
                              <button
                                type="button"
                                aria-expanded={isOpen}
                                onClick={() => setOpenClass(isOpen ? null : c.class)}
                                className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-2 px-3.5 py-2.5 text-left hover:bg-canvas"
                              >
                                <span className="flex min-w-0 items-center gap-2">
                                  {isOpen ? (
                                    <ChevronDown className="size-4 shrink-0" aria-hidden="true" />
                                  ) : (
                                    <ChevronRight className="size-4 shrink-0" aria-hidden="true" />
                                  )}
                                  <span className="min-w-0">
                                    {/* @DisplayName first: the same name as in Allure */}
                                    <span
                                      className="block text-[13px] font-medium text-ink"
                                      title={c.comment ?? undefined}
                                    >
                                      {c.displayName ?? c.comment ?? c.class}
                                    </span>
                                    <span className="block font-mono text-[11px] text-ink-subtle">
                                      {c.class}
                                      {c.cases.length
                                        ? ` · ${t('automation.runs.preflight.caseCount', { count: c.cases.length })}`
                                        : ''}
                                    </span>
                                  </span>
                                </span>
                                <span className="flex flex-wrap items-center gap-2">
                                  {freshInfo && !changed ? (
                                    <Badge className="bg-block-soft text-block">
                                      {t('automation.runs.preflight.badge.fresh')}
                                    </Badge>
                                  ) : null}
                                  {changed ? (
                                    autoPickedNow(c) ? (
                                      <AutoBadge
                                        kind={
                                          auto?.rotate.includes(c.class)
                                            ? 'rotate'
                                            : auto?.reg.includes(c.class)
                                              ? 'reg'
                                              : auto?.fresh.includes(c.class)
                                                ? 'freshPicked'
                                                : 'picked'
                                        }
                                      />
                                    ) : (
                                      <Badge className="bg-accent-soft text-accent-fg">
                                        {t('automation.runs.preflight.badge.changed')}
                                      </Badge>
                                    )
                                  ) : null}
                                  <FitBadge verdict={verdictFor(readiness, c.class, poolOf(c))} />
                                  <span className="font-mono text-[12px] text-ink-subtle">
                                    {effPhone(c)}
                                  </span>
                                </span>
                              </button>
                              <button
                                type="button"
                                onClick={() => exclude(c.class)}
                                title={t('automation.runs.preflight.exclude')}
                                aria-label={t('automation.runs.preflight.excludeLabel', {
                                  name: c.class,
                                })}
                                className="shrink-0 px-3 text-ink-subtle hover:bg-fail-soft hover:text-fail"
                              >
                                <X className="size-4" aria-hidden="true" />
                              </button>
                            </div>

                            {freshInfo ? (
                              <div
                                className={cn(
                                  'flex items-start gap-2 px-3.5 py-2 text-[12px] text-ink',
                                  changed ? 'bg-pass-soft' : 'bg-block-soft',
                                )}
                              >
                                <UserPlus
                                  className={cn(
                                    'mt-0.5 size-3.5 shrink-0',
                                    changed ? 'text-pass' : 'text-block',
                                  )}
                                  aria-hidden="true"
                                />
                                <p>
                                  <b>
                                    {changed
                                      ? t('automation.runs.preflight.fresh.resolved')
                                      : t('automation.runs.preflight.fresh.needed')}
                                  </b>{' '}
                                  {changed ? '' : `${t('automation.runs.preflight.fresh.action')} `}
                                  {freshInfo.cases && freshInfo.cases.length > 0
                                    ? `${t('automation.runs.preflight.fresh.destructive', {
                                        list: freshInfo.cases
                                          .map(
                                            (m) => c.cases.find((cs) => cs.method === m)?.name ?? m,
                                          )
                                          .join('; '),
                                      })} `
                                    : ''}
                                  {freshInfo.reason && !freshInfo.sources.includes('note') ? (
                                    // the heading already says «needs a new account» — the comment's same prefix goes
                                    <span className="text-ink-muted">
                                      {freshInfo.reason.replace(FRESH_REASON_PREFIX_RE, '')}{' '}
                                    </span>
                                  ) : null}
                                  <span className="text-ink-muted">
                                    {t('automation.runs.preflight.fresh.source', {
                                      list: freshInfo.sources
                                        .map((s) =>
                                          FRESH_SOURCES.has(s)
                                            ? t(
                                                `automation.runs.preflight.freshSource.${s as AutomationFreshSource}`,
                                              )
                                            : s,
                                        )
                                        .join(', '),
                                    })}
                                  </span>
                                </p>
                              </div>
                            ) : null}

                            {note ? (
                              <div className="flex items-start gap-2 bg-canvas px-3.5 py-2 text-[12px] text-ink">
                                <AlertTriangle
                                  className="mt-0.5 size-3.5 shrink-0 text-block"
                                  aria-hidden="true"
                                />
                                <p className="whitespace-pre-wrap">{note.text}</p>
                              </div>
                            ) : null}

                            {isOpen ? (
                              <div className="space-y-3 px-3.5 pb-3.5 pl-9 pt-1">
                                <div className="flex flex-wrap gap-2">
                                  <AccountField
                                    label={t('automation.runs.preflight.classField', {
                                      field: 'auth.phone',
                                    })}
                                    value={effPhone(c)}
                                    onChange={(value) => setOne(c, 'phone', value)}
                                  />
                                  <AccountField
                                    label="auth.iin"
                                    value={effIin(c)}
                                    onChange={(value) => setOne(c, 'iin', value)}
                                  />
                                  <AccountField
                                    label={
                                      <>
                                        auth.bin
                                        {c.account.bin
                                          ? null
                                          : ` (${t('automation.runs.segment.TOO')})`}
                                      </>
                                    }
                                    placeholder={
                                      c.account.bin
                                        ? undefined
                                        : t('automation.runs.preflight.binOnlyLlp')
                                    }
                                    value={effBin(c)}
                                    onChange={(value) => setOne(c, 'bin', value)}
                                  />
                                </div>
                                {!single && pool && pool.accounts.length > 0 ? (
                                  <PoolPicker
                                    label={t('automation.runs.preflight.pickerClass')}
                                    classes={[c]}
                                    segment={segmentOfClass(c)}
                                    pool={pool.accounts}
                                    readiness={readiness}
                                    current={poolOf(c)}
                                    onPick={(a) => pickFor([c], a)}
                                  />
                                ) : null}
                                <div className="flex flex-col gap-1">
                                  <SmallLabel htmlFor={`${noteId}-${c.class}`}>
                                    {t('automation.runs.preflight.note.label')}
                                  </SmallLabel>
                                  <Textarea
                                    id={`${noteId}-${c.class}`}
                                    rows={2}
                                    placeholder={t('automation.runs.preflight.note.placeholder')}
                                    value={draft}
                                    onChange={(event) =>
                                      setNoteDraft((d) => ({ ...d, [c.class]: event.target.value }))
                                    }
                                    className="text-[13px]"
                                  />
                                  <div className="mt-1 flex flex-wrap items-center gap-2">
                                    <Button
                                      size="sm"
                                      loading={saveNote.isPending}
                                      disabled={draft === (note?.text ?? '')}
                                      onClick={() => saveClassNote(c.class)}
                                    >
                                      <MessageSquarePlus className="size-3.5" aria-hidden="true" />
                                      {t('automation.runs.preflight.note.save')}
                                    </Button>
                                    {note ? (
                                      <span className="text-[11px] text-ink-subtle">
                                        {t('automation.runs.preflight.note.by', {
                                          by: note.updatedBy,
                                          date: date(note.updatedAt),
                                        })}
                                      </span>
                                    ) : null}
                                  </div>
                                </div>
                                <div>
                                  <p className="mb-1 text-[11px] text-ink-subtle">
                                    {t('automation.runs.preflight.cases', {
                                      count: c.cases.length,
                                    })}
                                  </p>
                                  <ol className="list-decimal space-y-1 pl-5 text-[12.5px] text-ink-muted">
                                    {c.cases.map((cs, i) => (
                                      <li key={i}>
                                        {cs.name}
                                        {cs.method && freshInfo?.cases?.includes(cs.method) ? (
                                          <Badge className="ml-1.5 bg-block-soft text-block">
                                            {t('automation.runs.preflight.destructive')}
                                          </Badge>
                                        ) : null}
                                      </li>
                                    ))}
                                  </ol>
                                  {c.file ? (
                                    <p className="mt-1 font-mono text-[11px] text-ink-subtle">
                                      {c.file}
                                    </p>
                                  ) : null}
                                </div>
                              </div>
                            ) : null}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>

          {excludedClasses.length > 0 ? (
            <div className="space-y-2 rounded-lg border border-dashed border-border px-3 py-2.5 text-[12.5px]">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-ink-muted">
                  {t('automation.runs.preflight.excludedTitle', { count: excludedClasses.length })}
                </span>
                <Button size="sm" onClick={() => setExcluded([])}>
                  <RotateCcw className="size-3.5" aria-hidden="true" />
                  {t('automation.runs.preflight.restoreAll')}
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {excludedClasses.map((c) => (
                  <Button
                    key={c.class}
                    size="sm"
                    title={t('automation.runs.preflight.restore')}
                    onClick={() => setExcluded((xs) => xs.filter((x) => x !== c.class))}
                  >
                    <span className="font-mono">{c.class}</span>
                    <RotateCcw className="size-3" aria-hidden="true" />
                  </Button>
                ))}
              </div>
            </div>
          ) : null}

          {blocked.length > 0 ? (
            <Notice tone="fail" icon={<UserPlus className="size-4" />}>
              <p>
                {t('automation.runs.preflight.blocked', { count: blocked.length })}{' '}
                {blocked.map((c) => (
                  <ClassLink key={c.class} name={c.class} onClick={() => setOpenClass(c.class)} />
                ))}
                {t('automation.runs.preflight.blockedHint')}
              </p>
            </Notice>
          ) : null}

          {runClasses.length > 0 ? (
            <div>
              <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink-subtle">
                <Terminal className="size-3.5" aria-hidden="true" />
                {t('automation.runs.preflight.command')}
              </p>
              <pre className="overflow-x-auto whitespace-pre-wrap break-all rounded-lg bg-sunken p-3 font-mono text-[12px] text-ink">
                {command}
              </pre>
            </div>
          ) : null}

          {rc && !rc.running && rc.problems.length > 0 ? (
            <div
              role="alert"
              className="flex items-start gap-2.5 rounded-lg border border-fail bg-fail-soft px-3 py-2.5"
            >
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-fail" aria-hidden="true" />
              <div className="min-w-0 space-y-1.5 text-[12.5px] text-ink">
                <p className="font-semibold">
                  {t('automation.runs.preflight.problemsTitle', { count: problemCount })}
                </p>
                {rc.problems.map((p) => (
                  <div key={`${p.account}|${p.text}`}>
                    <p>
                      <b>{p.account}</b> — {p.text}
                    </p>
                    <p>
                      {p.classes.map((cls) => (
                        <ClassLink key={cls} name={cls} onClick={() => setOpenClass(cls)} />
                      ))}
                    </p>
                  </div>
                ))}
                <p className="text-[12px] text-ink-muted">
                  {t('automation.runs.preflight.problemsHint')}
                  {rc.unknown > 0
                    ? ` ${t('automation.runs.preflight.problemsUnknown', { count: rc.unknown })}`
                    : ''}
                </p>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </Dialog>
  );
}

const AutoBadge = ({ kind }: { kind: 'rotate' | 'reg' | 'freshPicked' | 'picked' }) => {
  const { t } = useT();
  return (
    <Badge
      className="bg-pass-soft text-pass"
      title={t(`automation.runs.preflight.badge.${kind}Hint`)}
    >
      {t(`automation.runs.preflight.badge.${kind}`)}
    </Badge>
  );
};
